import axios from "axios";
import {
  basenameForWorkspace,
  resolveWorkspace,
} from "../utils/WorkspaceContext";
import { setGAUserId } from "../analytics/ga";
import { clearSharedSummary } from "../projects/swageazy/stores/alerts/alertsSummarySync";

export const IDENTITY_PATH_PREFIX = "/v1/identity/admin";

const ACCESS_TOKEN_KEY =
  process.env.REACT_APP_TOKEN_KEY || "Bearer_token#localhost";
const REFRESH_TOKEN_KEY =
  process.env.REACT_APP_REFRESH_TOKEN_KEY || `${ACCESS_TOKEN_KEY}_refresh`;

export interface SessionTokens {
  token?: string;
  authToken?: string;
  refreshToken?: string;
}

let refreshPromise: Promise<string> | null = null;
let isLoggingOut = false;

const toBearer = (token: string): string => {
  const trimmed = token.trim();
  if (!trimmed) return "";
  return /^bearer\s+/i.test(trimmed) ? trimmed : `Bearer ${trimmed}`;
};

export const getAccessToken = (): string => {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY) || "";
  } catch (error) {
    return "";
  }
};

export const getRefreshToken = (): string => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY) || "";
  } catch (error) {
    return "";
  }
};

export const getAuthorizationHeader = (): string => {
  const stored = getAccessToken();
  if (!stored) return "";
  return toBearer(stored);
};

export const persistSession = (result?: SessionTokens | null): void => {
  try {
    const jwt = result?.authToken || result?.token;
    if (jwt) {
      localStorage.setItem(ACCESS_TOKEN_KEY, toBearer(jwt));
    }
    if (result?.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, result.refreshToken);
    }
  } catch (error) {
    console.error(error);
  }
};

export const clearSession = (): void => {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    clearSharedSummary();
    setGAUserId(null);
  } catch (error) {
    console.error(error);
  }
};

const identityUrl = (path: string): string =>
  `${process.env.REACT_APP_API_URL || ""}${IDENTITY_PATH_PREFIX}${path}`;

const loginRedirectUrl = (): string => {
  const workspace = resolveWorkspace(window.location.pathname);
  return `${basenameForWorkspace(workspace)}/login`;
};

const isAuthPage = (): boolean => {
  const path = window.location.pathname;
  return path.endsWith("/login") || path.includes("/google-auth/");
};

export const forceLogout = async (): Promise<void> => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  const refreshToken = getRefreshToken();
  try {
    if (refreshToken) {
      await axios.post(
        identityUrl("/logout"),
        { refreshToken },
        { timeout: 15000 },
      );
    }
  } catch (error) {
    console.error(error);
  } finally {
    clearSession();
    if (!isAuthPage()) {
      window.location.href = loginRedirectUrl();
    } else {
      isLoggingOut = false;
    }
  }
};

// Only an explicit rejection means the refresh token is dead. Network errors,
// timeouts and 5xx are transient: the session must survive them.
const REFRESH_REJECTED_STATUSES = [400, 401, 403];

const isRefreshRejected = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  REFRESH_REJECTED_STATUSES.includes(error.response?.status ?? 0);

// `refreshPromise` only dedups concurrent refreshes within this tab. Two
// tabs racing a 401 at the same time would otherwise both POST /refresh with
// the same (about-to-be-rotated) refresh token, and the loser gets rejected
// and logged out even though the session is still good. The Web Locks API
// gives us a mutex across tabs/workers on the same origin: only one holder
// runs the network call at a time, and the lock is released automatically
// even if its tab crashes or is closed mid-refresh.
const REFRESH_LOCK_NAME = `${ACCESS_TOKEN_KEY}:refresh-lock`;

// A backgrounded tab can be frozen (Page Lifecycle API) rather than closed,
// which pauses its in-flight refresh call — and that call's own 15s timeout
// — while it still holds the lock. Bound how long another tab will wait for
// it so a frozen holder can't wedge everyone else indefinitely.
const REFRESH_LOCK_WAIT_MS = 20000;

const withCrossTabRefreshLock = <T>(fn: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks) {
    // Locks API unavailable (older Safari, non-browser test env): fall back
    // to in-tab-only dedup via refreshPromise above.
    return fn();
  }
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
    return locks.request(
      REFRESH_LOCK_NAME,
      { signal: AbortSignal.timeout(REFRESH_LOCK_WAIT_MS) },
      fn,
    );
  }
  return locks.request(REFRESH_LOCK_NAME, fn);
};

export const refreshAccessToken = (): Promise<string> => {
  if (refreshPromise) return refreshPromise;

  const tokenBeforeRefresh = getAuthorizationHeader();

  refreshPromise = (async () => {
    try {
      return await withCrossTabRefreshLock(async () => {
        // Another tab may have already refreshed and released the lock
        // while we were waiting for it — reuse that result instead of
        // burning a second refresh call (and a second refresh-token use).
        const currentToken = getAuthorizationHeader();
        if (currentToken && currentToken !== tokenBeforeRefresh) {
          return currentToken;
        }

        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          await forceLogout();
          throw new Error("No refresh token");
        }

        let result: SessionTokens;
        try {
          const response = await axios.post(
            identityUrl("/refresh"),
            {
              refreshToken,
            },
            { timeout: 15000 },
          );
          result = (response.data?.result || {}) as SessionTokens;
        } catch (error) {
          if (isRefreshRejected(error)) {
            await forceLogout();
          }
          throw error;
        }

        // Check the response, not storage: storage still holds the expired token.
        if (!(result.authToken || result.token)) {
          await forceLogout();
          throw new Error("Refresh did not return an access token");
        }

        persistSession(result);
        return getAuthorizationHeader();
      });
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};
